import React, {PureComponent} from 'react';
import PropTypes, {arrayOf, shape} from 'prop-types';
import {connect} from 'react-redux';
import cx from 'classnames';
import root from 'window-or-global';
import Slider from 'react-slick';
import {getAssetPath} from '../../utils/DieselUtils';
import {selectShouldDisableHeavyTransitions} from '../../redux/selectors/app.selectors';
import {carouselSlideType, CustomPropShapes, sizes, svgs, video} from '../../constants';
import Message from '../message/Message';
import Video from '../video/Video';
import Icon from '../icon/Icon';

import styles from './CarouselBase.pcss';

/*
This is using react-slick slider as a base with preset settings and styles according our needs in egstore.
To customize, you can pass react-slick settings into the "settings" prop, EXCEPT the settings listed in restrictedSettings.
*/

const mapStateToProps = state => ({
    disableSlideTransitions: selectShouldDisableHeavyTransitions(state),
    isRTL: state.getIn(['app', 'isRTL'])
});

class CarouselBase extends PureComponent {
    static propTypes = {
        className: PropTypes.string,
        disableSlideTransitions: PropTypes.bool,
        isRTL: PropTypes.bool,
        items: arrayOf(
            shape({
                type: CustomPropShapes.oneOfConstant(carouselSlideType).isRequired,
                props: PropTypes.object,
                children: PropTypes.node
            })
        ),
        settings: shape({
            arrowsClass: PropTypes.string,
            slidesClass: PropTypes.string
        })
    };

    static defaultProps = {
        settings: {}
    };

    component = React.createRef();
    refList = [];

    componentDidMount() {
        root.addEventListener('resize', this.onWindowResized);
        root.addEventListener('myExtraResize', this.onWindowResized);
    }

    componentWillUnmount() {
        root.removeEventListener('resize', this.onWindowResized);
        root.addEventListener('myExtraResize', this.onWindowResized);
        if (this.resizeForcer) {
            clearTimeout(this.resizeForcer);
        }
    }

    onWindowResized = e => {
        this.forceUpdate(); //react's default forceUpdate
        if (e.type === 'resize') {
            this.resizeForcer = setTimeout(this.followUpResize, 200);
        }
    };

    followUpResize = () => {
        const resizeEvent = root.document.createEvent('CustomEvent');
        resizeEvent.initCustomEvent('myExtraResize', false, false, {}); // all args required
        root.dispatchEvent(resizeEvent);
    };

    carouselItems = () => {
        const {isRTL, items} = this.props;

        if (isRTL) {
            const itemsList = Array.from(items);
            return itemsList.reverse();
        }

        return items;
    };

    getNewSettings = () => {
        const {settings} = this.props;

        const restrictedSettings = ['beforeChange', 'initialSlide', 'useCSS'];
        restrictedSettings.forEach(setting => {
            settings[setting] !== undefined &&
                console.warn(
                    `CarouselBase warning: ${setting} is a base setting and should not be overwritten.`
                );
        });

        const {
            /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
            arrowsClass,
            slidesClass,
            beforeChange,
            className,
            dotsClass,
            initialSlide,
            useCSS,
            /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */
            ...allowedSettings
        } = settings;

        return allowedSettings;
    };

    renderSlide = (item, index) => {
        const {isRTL, items} = this.props;

        const setRef = el => {
            this.refList.push(el);
        };
        const {props: imgProps} = item;
        if (item.type === carouselSlideType.IMAGE) {
            return (
                <div
                    className={styles.image}
                    style={{backgroundImage: `url(${getAssetPath(imgProps.src)})`}}
                    alt={imgProps.altText}
                    ref={setRef}
                />
            );
        }
        /* eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars */
        const {autoplay, hasFullScreen, ...videoProps} = item.props;

        // TODO: replace this with a "VideoSlide" that shows a poster and then loads the video when its the active slide.
        return (
            <Video
                ref={setRef}
                {...videoProps}
                autoplay={isRTL ? index === items.length - 1 : index === 0}
                hasFullScreen
            />
        );
    };

    handleVideoSlide = (oldIndex, newIndex) => {
        const numSlides = this.props.items.length;
        const {PLAY, PAUSE} = video.commands;
        const newSlideIndex = newIndex + numSlides;
        const newSlide = this.refList[newSlideIndex];

        if (newSlide && newSlide.videoRef) {
            newSlide[PLAY]();
        }

        // stop all other videos... event the clones
        this.refList.map((item, i) => {
            if (item && item !== newSlide && item.videoRef) {
                item[PAUSE]();
            }
        });
    };

    goToPrevSlide = () => this.component.current.slickPrev();
    goToNextSlide = () => this.component.current.slickNext();

    renderArrow = labels => {
        const {
            isRTL,
            settings: {arrowsClass}
        } = this.props;

        const mapOnArrowClick = {
            prev: isRTL ? this.goToNextSlide : this.goToPrevSlide,
            next: isRTL ? this.goToPrevSlide : this.goToNextSlide
        };

        return labels.map(label => {
            return (
                <button
                    key={label}
                    className={cx(arrowsClass, styles.button, styles[label])}
                    onClick={mapOnArrowClick[label]}>
                    <Message
                        className={styles.buttonLabel}
                        code={`diesel.common.aria.carousel.${label}`}
                    />
                    <Icon className={styles.icon} icon={svgs.CHEVRON_THIN} size={sizes.XXSMALL} />
                </button>
            );
        });
    };

    render() {
        const items = this.carouselItems();
        if (!items.length) return null;
        const {className, disableSlideTransitions, isRTL, settings} = this.props;

        const newSettings = this.getNewSettings();

        const baseSettings = {
            arrows: false,
            centerMode: true,
            centerPadding: 0,
            dots: true,
            dotsClass: cx(settings.dotsClass, styles.dots),
            draggable: false,
            beforeChange: (oldIndex, newIndex) => this.handleVideoSlide(oldIndex, newIndex),
            className: cx(settings.className, styles.base),
            initialSlide: isRTL ? items.length - 1 : 0,
            speed: 300,
            useCSS: !disableSlideTransitions,
            variableWidth: true,
            ...newSettings
        };

        const hasButtons = items.length > 1;

        return (
            <div className={cx(className, styles.wrapper)}>
                {hasButtons && this.renderArrow(['prev', 'next'])}
                <Slider {...baseSettings} ref={this.component}>
                    {items.map((item, i) => (
                        <div
                            className={cx(settings.slidesClass, styles.contentWrapper)}
                            key={`slide-${i}`}>
                            {this.renderSlide(item, i)}
                            {item.children}
                        </div>
                    ))}
                </Slider>
            </div>
        );
    }
}

export default connect(
    mapStateToProps,
    null,
    null,
    {forwardRef: true}
)(CarouselBase);
